/**
 * Gradien orb di ruang warna OKLCH.
 *
 * Interpolasi dilakukan di OKLCH, bukan RGB, karena jalur antar dua warna
 * berjauhan (mis. biru tua → biru terang, atau ungu → oranye) melewati
 * kecerahan yang terjaga; di RGB jalur yang sama melewati abu/cokelat keruh.
 * Efeknya: dua stop sudah cukup untuk kesan tiga warna, sehingga admin tidak
 * perlu menyetel stop ketiga.
 *
 * Fungsi murni tanpa DOM — bisa diuji langsung dengan `bun:test`.
 */

export interface Rgb {
	r: number;
	g: number;
	b: number;
}

export interface Oklch {
	/** Kecerahan 0–1. */
	l: number;
	/** Kroma (saturasi) 0–~0.4. */
	c: number;
	/** Hue dalam radian. */
	h: number;
}

const srgbToLinear = (value: number): number =>
	value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;

const linearToSrgb = (value: number): number =>
	value <= 0.0031308 ? value * 12.92 : 1.055 * value ** (1 / 2.4) - 0.055;

const clampByte = (value: number): number =>
	Math.max(0, Math.min(255, Math.round(value)));

/** `#1E3766` → komponen RGB 0–255. Mengembalikan null bila bukan hex 6 digit. */
export function hexToRgb(hex: string): Rgb | null {
	if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
	const n = Number.parseInt(hex.slice(1), 16);
	return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function rgbToHex({ r, g, b }: Rgb): string {
	const hex = ((clampByte(r) << 16) | (clampByte(g) << 8) | clampByte(b))
		.toString(16)
		.padStart(6, "0");
	return `#${hex.toUpperCase()}`;
}

export function rgbToOklch({ r, g, b }: Rgb): Oklch {
	const lr = srgbToLinear(r / 255);
	const lg = srgbToLinear(g / 255);
	const lb = srgbToLinear(b / 255);
	const l = Math.cbrt(
		0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb,
	);
	const m = Math.cbrt(
		0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb,
	);
	const s = Math.cbrt(
		0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb,
	);
	const labL = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
	const labA = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
	const labB = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
	return { l: labL, c: Math.hypot(labA, labB), h: Math.atan2(labB, labA) };
}

export function oklchToRgb({ l, c, h }: Oklch): Rgb {
	const labA = Math.cos(h) * c;
	const labB = Math.sin(h) * c;
	const lCube = (l + 0.3963377774 * labA + 0.2158037573 * labB) ** 3;
	const mCube = (l - 0.1055613458 * labA - 0.0638541728 * labB) ** 3;
	const sCube = (l - 0.0894841775 * labA - 1.291485548 * labB) ** 3;
	return {
		r: clampByte(
			linearToSrgb(
				4.0767416621 * lCube - 3.3077115913 * mCube + 0.2309699292 * sCube,
			) * 255,
		),
		g: clampByte(
			linearToSrgb(
				-1.2684380046 * lCube + 2.6097574011 * mCube - 0.3413193965 * sCube,
			) * 255,
		),
		b: clampByte(
			linearToSrgb(
				-0.0041960863 * lCube - 0.7034186147 * mCube + 1.707614701 * sCube,
			) * 255,
		),
	};
}

/** Selisih hue terpendek pada lingkaran, agar gradien tidak memutar jalur jauh. */
export function shortestHueDelta(from: number, to: number): number {
	let delta = (to - from) % (Math.PI * 2);
	if (delta > Math.PI) delta -= Math.PI * 2;
	if (delta < -Math.PI) delta += Math.PI * 2;
	return delta;
}

/**
 * Penyesuaian tema terang: orb bercahaya dirancang untuk latar gelap, dan di
 * latar putih warna neon jadi pucat. Kecerahan diturunkan dan kroma dinaikkan
 * supaya tetap terbaca — diturunkan otomatis dari warna pilihan admin, bukan
 * setelan terpisah.
 */
const LIGHT_LIGHTNESS_FACTOR = 0.82;
const LIGHT_CHROMA_FACTOR = 1.25;
const MAX_CHROMA = 0.37;

export const DEFAULT_RAMP_STEPS = 64;

/**
 * Tangga warna siap pakai dari `colorFrom` ke `colorTo`. Renderer mengambil
 * warna lewat indeks, jadi konversi OKLCH hanya dihitung sekali per perubahan
 * konfigurasi — bukan tiap frame, apalagi tiap titik.
 *
 * Hex yang tidak sah diganti warna netral, bukan melempar: konfigurasi rusak
 * di DB tidak boleh membuat logo gagal tampil.
 */
export function buildColorRamp(
	colorFrom: string,
	colorTo: string,
	light: boolean,
	steps: number = DEFAULT_RAMP_STEPS,
): readonly Rgb[] {
	const fallback: Rgb = { r: 136, g: 136, b: 136 };
	const fromRgb = hexToRgb(colorFrom) ?? fallback;
	const toRgb = hexToRgb(colorTo) ?? fallback;
	const from = rgbToOklch(fromRgb);
	const to = rgbToOklch(toRgb);
	const hueDelta = shortestHueDelta(from.h, to.h);
	const total = Math.max(2, Math.floor(steps));
	const ramp: Rgb[] = [];
	for (let i = 0; i < total; i++) {
		const t = i / (total - 1);
		let l = from.l + (to.l - from.l) * t;
		let c = from.c + (to.c - from.c) * t;
		if (light) {
			l *= LIGHT_LIGHTNESS_FACTOR;
			c = Math.min(MAX_CHROMA, c * LIGHT_CHROMA_FACTOR);
		}
		ramp.push(oklchToRgb({ l, c, h: from.h + hueDelta * t }));
	}
	return ramp;
}

/** Warna pada posisi `t` (0–1) di tangga warna. */
export function sampleRamp(ramp: readonly Rgb[], t: number): Rgb {
	if (ramp.length === 0) return { r: 136, g: 136, b: 136 };
	const clamped = Math.max(0, Math.min(1, Number.isFinite(t) ? t : 0));
	const index = Math.min(
		ramp.length - 1,
		Math.round(clamped * (ramp.length - 1)),
	);
	return ramp[index] as Rgb;
}

export function rgba({ r, g, b }: Rgb, alpha: number): string {
	const a = Math.max(0, Math.min(1, alpha));
	return `rgba(${r},${g},${b},${a})`;
}
